import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import { createCanonicalChanges, resolveCompareOptions } from "@ext/git/core/Diff/logic/canonicalChanges";
import changeStops from "@ext/git/core/Diff/logic/changeStops";
import collectComments from "@ext/git/core/Diff/logic/commentsDiff/collectComments";
import getCommentDiffLines from "@ext/git/core/Diff/logic/commentsDiff/getCommentDiffLines";
import useCommentsDiff from "@ext/git/core/Diff/logic/commentsDiff/useCommentsDiff";
import type { CommentDiffLine } from "@ext/git/core/Diff/logic/model/DiffLine";
import type { ProseMirrorDiffLine } from "@ext/git/core/Diff/logic/model/ProseMirrorDiffLine";
import movedMarkers from "@ext/git/core/Diff/logic/movedMarkers";
import { onlyChangesKey } from "@ext/git/core/Diff/logic/onlyChanges";
import toDiffLines from "@ext/git/core/Diff/logic/toDiffLines";
import { CommentEditorEventsContext } from "@ext/markdown/elements/comment/edit/logic/CommentEditorProvider";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Editor } from "@tiptap/react";
import type { Node } from "prosemirror-model";
import { useContext, useEffect, useRef } from "react";

interface UseDiffProps {
	newEditor: Editor;
	baseline: () => Node | null;
	newApiUrlCreator: ApiUrlCreator;
	oldApiUrlCreator: ApiUrlCreator | null;
}

const inside = (lines: CommentDiffLine[], doc: Node) => lines.filter((line) => line.pos.to < doc.content.size);

const useDiff = (props: UseDiffProps) => {
	const { newEditor, baseline, newApiUrlCreator, oldApiUrlCreator } = props;

	const commentEditorEvents = useContext(CommentEditorEventsContext);
	const getCommentsDiff = useCommentsDiff(newApiUrlCreator, oldApiUrlCreator);
	const lastChanges = useRef<DiffChange[] | undefined>(undefined);
	// Bumped on every recompute and on unsubscribe: a late comments response
	// must not bring stale lines back.
	const lastGeneration = useRef(0);
	const lastCommentLines = useRef<CommentDiffLine[]>([]);
	const canonical = useRef(createCanonicalChanges());

	const publish = (changes: DiffChange[], diffLines: ProseMirrorDiffLine[], before: Node, after: Node) => {
		const markers = movedMarkers(changes, before, after);

		newEditor
			.chain()
			.updateMovedMarkers(markers)
			.updateDiffLinesModel([...diffLines, ...inside(lastCommentLines.current, after)])
			.updateDiffChanges(changes)
			.command(({ tr }) => {
				tr.setMeta(onlyChangesKey, {
					type: "model",
					stops: changeStops(changes),
					keepVisible: markers.filter((marker) => marker.side === "from").map((marker) => marker.pos),
				});
				return true;
			})
			.run();
	};

	const updateDiffLines = async (libraryChanges?: DiffChange[]) => {
		if (!newEditor || newEditor.isDestroyed || !baseline) return;

		const before = baseline();
		if (!before) return;

		const generation = ++lastGeneration.current;
		const after = newEditor.state.doc;
		const options = resolveCompareOptions();

		const changes = libraryChanges
			? canonical.current.adopt(before, after, options, libraryChanges)
			: canonical.current.resolve(before, after, newEditor.schema, options);
		const diffLines = toDiffLines(changes, { before, after });

		publish(changes, diffLines, before, after);

		if (!oldApiUrlCreator) return;
		const paragraphCommentsDiff = await getCommentsDiff(collectComments(before), collectComments(after));

		// The article may have been closed or recomputed while the request was in flight.
		if (newEditor.isDestroyed || generation !== lastGeneration.current) return;

		lastCommentLines.current = getCommentDiffLines(paragraphCommentsDiff, before, after, changes);
		newEditor.commands.updateDiffLinesModel([...diffLines, ...lastCommentLines.current]);
	};

	// No debounce of our own: a second timer races the library's and reads a stale result.
	// Listen to `transaction`, not `update`: the library publishes via a doc-preserving transaction.
	// `storage.diff.changes` matches the current doc only inside this event; later the debounce leaves it stale.
	const onTransaction = () => {
		const changes = newEditor?.storage.diff?.changes;
		if (changes === lastChanges.current) return;

		lastChanges.current = changes;
		void updateDiffLines(changes);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: recompute is bound to editor and baseline; the rest is read at call time
	useEffect(() => {
		if (!newEditor || newEditor.isDestroyed || !baseline || !commentEditorEvents) return;
		lastChanges.current = newEditor.storage.diff?.changes;
		void updateDiffLines();

		newEditor.on("transaction", onTransaction);
		const commentEditorEventsTokens = [
			commentEditorEvents.on("update", () => void updateDiffLines()),
			commentEditorEvents.on("delete", () => void updateDiffLines()),
		];

		return () => {
			lastGeneration.current++;
			lastCommentLines.current = [];
			newEditor.off("transaction", onTransaction);
			commentEditorEventsTokens.forEach((token) => commentEditorEvents.off(token));
		};
	}, [newEditor, baseline, commentEditorEvents]);
};

export default useDiff;
